import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { CheckoutStepService } from "@spartacus/checkout/base/components";
import { ActivatedRoute } from "@angular/router";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { catchError, first, switchMap, take, tap } from "rxjs/operators";
import { BehaviorSubject, Observable, Subscription, EMPTY } from "rxjs";
import { User, UserAccountFacade } from '@spartacus/user/account/root';
import { GenActiveCartService } from '../../cart/core/facade/active-cart.service';
import { WindowRef } from '@spartacus/core';
import { GenCheckoutService } from 'src/app/generac/services/checkout.service';
import { Cart } from '@spartacus/cart/base/root';
import { GenEngineReplacementForm } from 'src/app/generac/models/checkout.model';

@Component({
  selector: 'gen-checkout-engine-replacement-form',
  templateUrl: './checkout-engine-replacement-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GenCheckoutEngineReplacementFormComponent implements OnInit, OnDestroy {
  private formGroupSubscription: Subscription;
  private cartNumber: string;

  engineReplacementFormGroup: FormGroup<any> = new FormGroup({});
  engineReplacementForm$ = new BehaviorSubject<GenEngineReplacementForm>(null);
  currentUser: User;
  internalCalculation: boolean;
  skipPaymentTypeStep: boolean = false;
  isServicePartsEnabled: boolean;
  dealerId: string;
  engineReplacementModelNumber: string;
  attestationCheckboxesFormControls: string[] = [
    'PERFORMANCE',
    'PACKAGING_OR_FIT',
  ];
  packagingFitOptions: string[] = [
    'electricalConnections',
    'aftertreatmentPlacement',
    'equipmentEngineInterface',
    'mountingLocations'
  ];
  packagingFitEquipmentEngineInterfaceSubOptions: string[] = [
    'equipmentEngineInterfaceOptions.physical',
    'equipmentEngineInterfaceOptions.electrical',
  ];
  destroyedByOptionsControls: string[] = [
    'DESTROYED_BEYOND_REPAIR_SCRAPPED',
    'DESTROYED_BY_PIERCING_OR_DRILLING',
    'RETURNED_TO_GENERAC',
  ];

  constructor(
    protected checkoutStepService: CheckoutStepService,
    protected activatedRoute: ActivatedRoute,
    protected winRef: WindowRef,
    private checkoutService: GenCheckoutService,
    private activeCartFacade: GenActiveCartService,
    private userAccount: UserAccountFacade,
  ) {
  }

  ngOnInit(): void {
    this.isServicePartsEnabled = JSON.parse(this.winRef.localStorage.getItem('isServicePartsEnabled'));
    this.dealerId = this.winRef.localStorage.getItem('companyNumber');

    this.userAccount.get().pipe(
      first(),
      switchMap(user => {
        this.currentUser = user;
        return this.initEngineReplacementAttestation();
      }))
      .subscribe();
  }

  changeReplacementReason(replacementReason: string): void {
    this.engineReplacementFormGroup.get('replacementReason').setValue(replacementReason);
  }

  changeDestructionType(destructionType: string): void {
    this.engineReplacementFormGroup.get('destructionType').setValue(destructionType);
  }

  updateEngineReplacementAttestation(engineReplacementForm: GenEngineReplacementForm): void {
    if (!this.engineReplacementFormGroup.get('destructionType').value) {
      this.engineReplacementFormGroup.controls.destructionType.markAsTouched();
      this.engineReplacementFormGroup.controls.destructionType.setErrors({ destructionTypeRequired: true });
      return;
    }

    this.checkoutService.updateEngineReplacementAttestation(this.cartNumber, this.currentUser.uid, engineReplacementForm)
      .pipe(
        take(1),
        tap(() => {
          this.checkoutStepService.next(this.activatedRoute);
        }),
        catchError((error: any) => {
          const index = error?.error.errors.indexOf(error?.error.errors?.find((err: any) => err.type === 'IllegalStateError' && err.message.startsWith('The Serial Number entered is not valid.')));
          if (index > -1) {
            this.engineReplacementFormGroup.controls.engineSerialNumber.setErrors({ invalidSerialNumber: true });
          }
          return EMPTY;
        })
      )
      .subscribe();
  }

  private initEngineReplacementAttestation(): Observable<GenEngineReplacementForm> {
    return this.activeCartFacade.getActive().pipe(
      take(1),
      switchMap((cart: Cart) => {
        this.engineReplacementModelNumber = cart.entries?.find(e => !!e.product.engineReplacement).product?.code;
        this.cartNumber = cart.code;
        this.skipPaymentTypeStep = cart?.wellsFargoCart;
        this.checkoutService.disableCheckoutSteps(cart.cartTypes, cart?.engineReplacement);
        this.internalCalculation = cart.internalCalculation;
        return this.getEngineReplacementAttestation(cart.code, this.currentUser.uid);
      }),
      tap((options: GenEngineReplacementForm) => {
        const data = {
          ...options,
          engineModelNumber: this.engineReplacementModelNumber || options?.engineModelNumber
        }
        this.engineReplacementForm$.next(data);
        this.initFormGroup(data);
      })
    );
  }

  private getEngineReplacementAttestation(cartNumber: string, userId: string): Observable<GenEngineReplacementForm> {
    return this.checkoutService.getEngineReplacementAttestation(cartNumber, userId);
  }

  private initFormGroup(engineReplacementForm: GenEngineReplacementForm) {
    const rawDate = engineReplacementForm?.purchaseDate ? new Date(engineReplacementForm.purchaseDate) : new Date();
    const pad = (n: number) => ('0' + n).slice(-2);
    const formattedPurchaseDate = `${pad(rawDate.getMonth() + 1)}.${pad(rawDate.getDate())}.${rawDate.getFullYear()}`;

    this.engineReplacementFormGroup = new FormGroup({
      dealerId: new FormControl({ value: engineReplacementForm?.dealerId || this.dealerId, disabled: true }),
      engineModelNumber: new FormControl({ value: engineReplacementForm?.engineModelNumber || '', disabled: true }, Validators.required),
      engineSerialNumber: new FormControl({ value: engineReplacementForm?.engineSerialNumber || '', disabled: false }, Validators.required),
      purchaseDate: new FormControl({ value: formattedPurchaseDate, disabled: true }),
      purchasingUser: new FormControl({ value: engineReplacementForm?.purchasingUser || this.currentUser.uid, disabled: true }),
      replacementReason: new FormControl({ value: engineReplacementForm?.replacementReason || this.attestationCheckboxesFormControls[1], disabled: false }, Validators.required),
      destructionType: new FormControl({ value: engineReplacementForm?.destructionType || '', disabled: false }),
    });
  }

  ngOnDestroy(): void {
    if (this.formGroupSubscription) {
      this.formGroupSubscription.unsubscribe();
    }
  }
}
